import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Collapse,
  Fade,
  alpha
} from "@mui/material";
import {
  CloudUpload,
  Description,
  CheckCircle,
  Error,
  ExpandMore,
  Close,
  InsertDriveFile
} from "@mui/icons-material";
import { uploadBatchCsv } from "../../../../../Reducer/productBatchesSlice";
import EmailVerifierCsv from "./EmailVerifierCsv";

const UploadBatchCsv = ({ batch }) => {
  const [file, setFile] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const[showVerifier, setShowVerifier] = useState(false);
  const fileInputRef = useRef(null);

  const dispatch = useDispatch();
  const { loading, error, uploadStatus } = useSelector((state) => state.productBatches);
  const [report, setReport] = useState(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      const droppedFile = droppedFiles[0];
      if (droppedFile.type === "text/csv" || droppedFile.name.endsWith('.csv')) {
        setFile(droppedFile);
        setReport(null);
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
      setReport(null);
    }
  };

  const handleUpload = () => {
    if (!file) return;

    dispatch(uploadBatchCsv({ batch, file }))
      .unwrap()
      .then((res) => {
        if (res.success) {
          setReport(res);
          setIsExpanded(true);
        }
      })
      .catch(() => {
        setReport(null);
      });
  };

  const removeFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSelectFileClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      <Typography variant="h5" fontWeight="600" gutterBottom color="primary">
        Upload CSV File
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Upload a CSV file for{" "}
        <Box component="span" fontWeight="600">
          {batch.product?.event_name} - {batch.variant?.name}
        </Box>
      </Typography>

      {/* Custom drag and drop area */}
      {!file && (
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleSelectFileClick}
          sx={{
            border: `2px dashed ${isDragging ? 'primary.main' : 'grey.400'}`,
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            backgroundColor: isDragging ? alpha('#2196f3', 0.08) : 'background.default',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            mb: 3,
            '&:hover': {
              backgroundColor: 'action.hover',
              borderColor: 'primary.main'
            }
          }}
        >
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            ref={fileInputRef}
            style={{ display: 'none' }}
          />
          <CloudUpload sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            {isDragging ? 'Drop your CSV file here' : 'Select or drag & drop your CSV file'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Only CSV files are accepted
          </Typography>
        </Box>
      )}

      {/* Selected file preview */}
      {file && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center">
                <Description color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="body1" fontWeight="500">
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(file.size / 1024).toFixed(2)} KB
                  </Typography>
                </Box>
              </Box>
              <IconButton size="small" onClick={removeFile}>
                <Close />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Error Message */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
            >
              <Close fontSize="inherit" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      {/* Upload Button */}
      <Box display="flex" gap={2} alignItems="center">
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={loading || !file}
          startIcon={!loading && <CloudUpload />}
          size="large"
        >
          {loading ? <CircularProgress size={20} /> : "Upload CSV"}
        </Button>
        <Button
          variant="outlined"
          onClick={removeFile}
          disabled={!file || loading}
        >
          Cancel
        </Button>
      {/* </Box> */}
      {/* 🔹 New Optional Button */}
  <Button
    variant="contained"
    color="secondary"
    disabled={!file}
    onClick={() => setShowVerifier(true)}
  >
    Verify Emails by DeBounce
  </Button>
</Box>

{/* Show verifier component */}
{showVerifier && (
  <EmailVerifierCsv file={file} onClose={() => setShowVerifier(false)} batch={batch} />
)}

      {/* Success Report */}
      <Collapse in={uploadStatus === "success" && report} timeout={500}>
        <Fade in={uploadStatus === "success" && report} timeout={500}>
          <Paper 
            elevation={2} 
            sx={{ 
              mt: 4, 
              p: 0, 
              borderRadius: 2, 
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'success.light'
            }}
          >
            <Box 
              sx={{ 
                backgroundColor: 'success.light', 
                color: 'success.contrastText', 
                p: 2,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <CheckCircle sx={{ mr: 1 }} />
              <Typography variant="h6">
                {report?.message || "File uploaded successfully!"}
              </Typography>
              <IconButton 
                size="small" 
                sx={{ ml: 'auto', color: 'inherit' }}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <ExpandMore sx={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }} />
              </IconButton>
            </Box>
            
            <Collapse in={isExpanded}>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Inserted Records
                    </Typography>
                    <Chip 
                      label={report?.insertedRecords} 
                      color="primary" 
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Batch ID
                    </Typography>
                    <Typography variant="body1">
                      {report?.batch?.id}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Exhibitors Count
                    </Typography>
                    <Chip 
                      label={report?.batch?.exhibitors_count} 
                      color="secondary" 
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Available Count
                    </Typography>
                    <Chip 
                      label={report?.batch?.available_count} 
                      color="success" 
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Deficit Count
                    </Typography>
                    <Chip 
                      label={report?.batch?.deficiate_count} 
                      color="warning" 
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Fulfilled Count
                    </Typography>
                    <Chip 
                      label={report?.batch?.fulfilled_count} 
                      color="info" 
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Delivery Status
                    </Typography>
                    <Chip 
                      label={report?.batch?.deliveryStatus} 
                      color={
                        report?.batch?.deliveryStatus === 'completed' ? 'success' : 'default'
                      } 
                      icon={
                        report?.batch?.deliveryStatus === 'completed' ? <CheckCircle /> : <Error />
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Paper>
        </Fade>
      </Collapse>
    </Box>
  );
};

export default UploadBatchCsv;